import { Link } from "react-router-dom";
import { ArrowRight, Bath, BedDouble, CookingPot, MapPin, MessageCircle, Truck } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/motion";
import { useSeo } from "@/lib/seo";

const UNIVERSES = [
  {
    icon: CookingPot,
    title: "Cuisines équipées",
    text: "Mitigeurs, robinetterie et équipements haut de gamme pour une cuisine équipée fonctionnelle et élégante, pensée pour les habitudes des familles sénégalaises.",
  },
  {
    icon: Bath,
    title: "Salles de bain modernes",
    text: "Meubles vasque, meubles de douche, miroirs et accessoires : tout pour aménager une salle de bain moderne à Dakar, digne des plus beaux intérieurs.",
  },
  {
    icon: BedDouble,
    title: "Chambres modernes",
    text: "Lits coffres et rangements malins pour une chambre à coucher confortable et optimisée — le mobilier idéal pour les maisons sénégalaises.",
  },
];

const About = () => {
  const [phone, setPhone] = useState("");

  useSeo({
    title: "À propos — Boutique sénégalaise d'aménagement intérieur",
    description:
      "Sama Keur Shop, boutique sénégalaise spécialisée en aménagement intérieur : cuisines équipées, salles de bain modernes et chambres à coucher. Livraison partout au Sénégal, showroom à Dakar.",
    keywords:
      "meubles Dakar, cuisine équipée Sénégal, salle de bain moderne Dakar, chambre à coucher Sénégal, showroom Dakar, aménagement intérieur Sénégal",
    path: "/a-propos",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setPhone((r.data.phone || "").replace(/[^0-9]/g, ""))).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page" className="bg-sand">
      {/* HERO */}
      <section className="relative overflow-hidden bg-bark text-sand">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-6 text-xs font-semibold uppercase tracking-[0.35em] text-terracotta-light"
          >
            À propos de Sama Keur Shop
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl"
          >
            Une boutique <span className="italic text-terracotta-light">100 % sénégalaise</span> pour votre intérieur
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-6 max-w-2xl text-sm leading-relaxed text-sand/75 sm:text-base"
          >
            « Sama keur » — ma maison, en wolof. Depuis Dakar, nous sélectionnons des meubles et équipements
            de qualité pour aménager les cuisines, les salles de bain et les chambres des maisons sénégalaises.
            Chaque pièce est choisie avec soin, livrée partout au Sénégal, avec la teranga en plus.
          </motion.p>
        </div>
        <p className="pointer-events-none absolute -bottom-8 right-0 select-none whitespace-nowrap font-display text-[14vw] font-bold leading-none text-sand/5">
          teranga
        </p>
      </section>

      {/* UNIVERS */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <Reveal>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Nos spécialités</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl">
            L'aménagement intérieur, notre métier
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {UNIVERSES.map((u) => (
            <Reveal key={u.title}>
              <div className="flex h-full flex-col border border-clay bg-cream p-8 transition-colors duration-300 hover:border-terracotta">
                <u.icon className="h-8 w-8 text-terracotta" />
                <h3 className="mt-5 font-display text-2xl font-semibold text-bark">{u.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-bark/65">{u.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LIVRAISON + SHOWROOM */}
      <section className="border-y border-clay bg-cream">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div>
              <Truck className="h-8 w-8 text-terracotta" />
              <h2 className="mt-5 font-display text-3xl font-semibold text-bark">Livraison partout au Sénégal</h2>
              <p className="mt-4 text-sm leading-relaxed text-bark/65">
                Basés à Dakar, nous livrons dans toute la capitale et sa banlieue, mais aussi à Thiès, Mbour,
                Saint-Louis et dans les autres villes du Sénégal. Paiement simple par Wave, Orange Money ou carte
                bancaire — et possibilité de payer en deux fois.
              </p>
              <Link
                to="/boutique"
                data-testid="about-cta-boutique"
                className="group mt-8 inline-flex h-12 items-center gap-3 rounded-full bg-terracotta px-7 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark"
              >
                Découvrir la boutique
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <MapPin className="h-8 w-8 text-terracotta" />
              <h2 className="mt-5 font-display text-3xl font-semibold text-bark">Bientôt : notre showroom à Dakar</h2>
              <p className="mt-4 text-sm leading-relaxed text-bark/65">
                Notre showroom ouvrira prochainement ses portes à Dakar : vous pourrez toucher les matériaux,
                comparer les finitions et repartir avec des conseils personnalisés. Suivez-nous — l'ouverture
                arrive très vite !
              </p>
              {phone && (
                <a
                  href={`https://wa.me/${phone}?text=${encodeURIComponent("Bonjour Sama Keur Shop ! Je souhaite des informations sur vos produits.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-whatsapp"
                  className="mt-8 inline-flex h-12 items-center gap-3 rounded-full border border-bark px-7 text-xs font-bold uppercase tracking-[0.15em] text-bark transition-colors hover:border-terracotta hover:text-terracotta"
                >
                  <MessageCircle className="h-4 w-4" /> Nous écrire sur WhatsApp
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CHIFFRES / CONFIANCE */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <Reveal>
          <div className="grid gap-6 text-center sm:grid-cols-3">
            {[
              ["56+", "produits sélectionnés"],
              ["4", "villes desservies en direct"],
              ["100 %", "paiement sécurisé"],
            ].map(([n, label]) => (
              <div key={label} className="border border-clay bg-cream px-6 py-10">
                <p className="font-display text-5xl font-bold text-terracotta">{n}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-bark/50">{label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
